import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import retry from 'async-retry';
import { AxiosError } from 'axios';

import Sentry from 'config/sentry';

import { AppDispatch } from 'containers/Femtasy';

import {
  CreatePortalSessionDocument,
  CreatePortalSessionMutation,
} from 'graphql/generated/graphql';

import { buildGraphaelQueryOptions } from 'hooks/useGraphaelDefaults';
import { apolloClient } from 'hooks/withGraphQL';

import { setProfileUser } from 'store/modules/profile';
import { GetReduxState } from 'store/ReduxState';

import { AuthStateHeaders } from './auth';
import * as ChargebeeModule from './chargebee';

export interface ChargebeeState {
  servicePortalError?: AxiosError;
  servicePortalOpen: boolean;
  servicePortalOpening: boolean;
}

export const initialChargebeeState: ChargebeeState = {
  servicePortalOpen: false,
  servicePortalOpening: false,
};

const chargebeeSlice = createSlice({
  name: 'chargebee',
  initialState: initialChargebeeState,
  reducers: {
    selfServiceClosed: (_state) => ({
      ...initialChargebeeState,
      servicePortalOpening: false,
      servicePortalOpen: false,
    }),
    selfServiceError: (state, action: PayloadAction<AxiosError>) => {
      state.servicePortalError = action.payload;
      state.servicePortalOpening = false;
    },
    selfServiceOpening: (_state) => ({
      ...initialChargebeeState,
      servicePortalOpening: true,
      servicePortalOpen: false,
    }),
    selfServiceLoaded: (_state) => ({
      ...initialChargebeeState,
      servicePortalOpen: true,
    }),
    reset: (_state) => ({ ...initialChargebeeState }),
  },
});

export default chargebeeSlice.reducer;

// Action creators
export const {
  selfServiceClosed,
  selfServiceError,
  selfServiceLoaded,
  selfServiceOpening,
  reset,
} = chargebeeSlice.actions;

// Side effect helpers
export const fetchPortalSession = async (
  isAuthenticated: boolean,
  headers: AuthStateHeaders,
  locale: string,
) => {
  const queryOptions = buildGraphaelQueryOptions(
    isAuthenticated,
    headers,
    locale,
  );

  const { data, errors } =
    await apolloClient.mutate<CreatePortalSessionMutation>({
      mutation: CreatePortalSessionDocument,
      ...queryOptions,
      fetchPolicy: 'no-cache',
    });

  if (data) {
    const parsedPortalSession = JSON.parse(data.createPortalSession.response);

    return parsedPortalSession.portal_session;
  } else {
    if (errors) errors.forEach((err) => Sentry.captureException(err));
  }
};

export const selfServicePortalCallbacks = (
  dispatch: AppDispatch,
  getState: GetReduxState,
) => ({
  error: (error) => {
    Sentry.captureException(error);
    dispatch(chargebeeSlice.actions.selfServiceError(error));
  },
  loaded: () => dispatch(chargebeeSlice.actions.selfServiceLoaded()),
  close: () => dispatch(chargebeeSlice.actions.selfServiceClosed()),
  scheduledCancellationRemoved: (_data) => {
    const user = getState().profile.user;

    if (!user || !user.activeSubscription) return;

    dispatch(
      setProfileUser({
        ...user,
        activeSubscription: {
          ...user.activeSubscription,
          cancellationScheduledAt: null,
        },
      }),
    );
  },
});

// To avoid error: Property 'Chargebee' does not exist on type 'Window & typeof
// globalThis'.
// declare const window: any;

const initializeChargebee = () => {
  if (window.Chargebee) {
    window.Chargebee.init({
      site: process.env.REACT_APP_CHARGEBEE_SITE_NAME,
    });

    const chargebeeInstance = window.Chargebee.getInstance();

    return chargebeeInstance;
  } else {
    throw new Error('Chargebee not initialized');
  }
};

export const openSelfServicePortal =
  () => async (dispatch: AppDispatch, getState: GetReduxState) => {
    dispatch(chargebeeSlice.actions.selfServiceOpening());

    const { isAuthenticated, headers } = getState().auth;

    const { locale } = getState().translations;

    try {
      const chargebeeInstance = await retry(initializeChargebee, {
        retries: 5,
      });

      chargebeeInstance.setPortalCallbacks(
        selfServicePortalCallbacks(dispatch, getState),
      );
      chargebeeInstance.setPortalSession(() =>
        ChargebeeModule.fetchPortalSession(
          isAuthenticated,
          headers as AuthStateHeaders,
          locale,
        ),
      );

      const portal = chargebeeInstance.createChargebeePortal();

      portal.open();
    } catch (error) {
      Sentry.captureException(error);
      dispatch(chargebeeSlice.actions.selfServiceClosed());
    }
  };

export const openSubscriptionDetailsPortal =
  () => async (dispatch: AppDispatch, getState: GetReduxState) => {
    dispatch(chargebeeSlice.actions.selfServiceOpening());

    const { isAuthenticated, headers } = getState().auth;

    const { locale } = getState().translations;

    try {
      const chargebeeInstance = await retry(initializeChargebee, {
        retries: 5,
      });

      chargebeeInstance.setPortalCallbacks(
        selfServicePortalCallbacks(dispatch, getState),
      );

      chargebeeInstance.setPortalSession(() =>
        ChargebeeModule.fetchPortalSession(
          isAuthenticated,
          headers as AuthStateHeaders,
          locale,
        ),
      );

      const portal = chargebeeInstance.createChargebeePortal();
      const pageToOpen =
        window.Chargebee.getPortalSections().SUBSCRIPTION_DETAILS;

      const subscriptionId =
        getState().profile.user?.activeSubscription?.subscriptionId;

      if (subscriptionId) {
        portal.open(
          {},
          {
            sectionType: pageToOpen,
            params: {
              subscriptionId: subscriptionId,
            },
          },
        );
      } else {
        portal.open();
      }
    } catch (error) {
      Sentry.captureException(error);
      dispatch(chargebeeSlice.actions.selfServiceClosed());
    }
  };
